import * as React from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { UpdateGapDeviceRequest } from "@/types/gap-device"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"

interface BulkUpdateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  deviceIds: string[]
  onConfirm: (data: UpdateGapDeviceRequest) => void
  isLoading?: boolean
}

export function BulkUpdateDialog({
  open,
  onOpenChange,
  deviceIds,
  onConfirm,
  isLoading = false,
}: BulkUpdateDialogProps) {
  const [showAllDevices, setShowAllDevices] = React.useState(false)
  const [necThreshold, setNecThreshold] = React.useState<string>("")
  const [necThresholdUseDefault, setNecThresholdUseDefault] = React.useState(false)
  const [userInstructions, setUserInstructions] = React.useState<"unchanged" | "true" | "false">("unchanged")
  const [userInstructionsUseDefault, setUserInstructionsUseDefault] = React.useState(false)
  const [necThresholdError, setNecThresholdError] = React.useState<string>("")

  const visibleDevices = showAllDevices ? deviceIds : deviceIds.slice(0, 5)
  const hasMoreDevices = deviceIds.length > 5

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setNecThreshold("")
      setNecThresholdUseDefault(false)
      setUserInstructions("unchanged")
      setUserInstructionsUseDefault(false)
      setNecThresholdError("")
      setShowAllDevices(false)
    }
  }, [open])

  const hasChanges =
    necThreshold !== "" ||
    necThresholdUseDefault ||
    userInstructions !== "unchanged" ||
    userInstructionsUseDefault

  const validateNecThreshold = (): boolean => {
    if (necThresholdUseDefault || necThreshold === "") return true
    const num = Number(necThreshold)
    if (Number.isNaN(num)) {
      setNecThresholdError("Threshold must be a number")
      return false
    }
    if (!Number.isInteger(num)) {
      setNecThresholdError("Threshold must be an integer")
      return false
    }
    if (num < 0 || num > 10000) {
      setNecThresholdError("Threshold must be between 0 and 10000")
      return false
    }
    setNecThresholdError("")
    return true
  }

  const handleConfirm = () => {
    if (!validateNecThreshold()) return
    const data: UpdateGapDeviceRequest = {}

    // Handle NEC Threshold
    if (necThresholdUseDefault) {
      // User wants to set all devices to use default
      data.necMatchScoreThreshold = { useDefault: true } // Omit value when using default
    } else if (necThreshold !== "") {
      // User provided a specific value
      data.necMatchScoreThreshold = { useDefault: false, value: Math.round(Number(necThreshold)) }
    }
    // If neither is set, leave field unchanged (don't include in data)

    // Handle User Instructions
    if (userInstructionsUseDefault) {
      // User wants to set all devices to use default
      data.featureFlagUserInstructions = { useDefault: true } // Omit value when using default
    } else if (userInstructions !== "unchanged") {
      // User selected a specific value
      data.featureFlagUserInstructions = { useDefault: false, value: userInstructions === "true" }
    }
    // If unchanged, leave field unchanged (don't include in data)

    onConfirm(data)
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-card border-border max-w-[450px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground">Bulk Update Configuration</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            Update configuration for{" "}
            <span className="font-semibold text-foreground">{deviceIds.length} devices</span>.
            Only changed fields will be updated.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Update form */}
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-foreground">NEC Match Score Threshold</Label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bulkNecThresholdUseDefault"
                  checked={necThresholdUseDefault}
                  onChange={(e) => {
                    setNecThresholdUseDefault(e.target.checked)
                    if (e.target.checked) {
                      setNecThreshold("")
                    }
                  }}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <Label htmlFor="bulkNecThresholdUseDefault" className="text-xs text-muted-foreground cursor-pointer">
                  Use default
                </Label>
              </div>
            </div>
            <Input
              type="number"
              min={0}
              max={10000}
              step={1}
              value={necThreshold}
              onChange={(e) => {
                setNecThreshold(e.target.value)
                setNecThresholdError("")
                if (e.target.value !== "") {
                  setNecThresholdUseDefault(false)
                }
              }}
              onBlur={() => validateNecThreshold()}
              placeholder="Leave blank to keep unchanged"
              disabled={necThresholdUseDefault}
              autoComplete="off"
              className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed ${
                necThresholdError ? "border-destructive" : ""
              }`}
            />
            {necThresholdError ? (
              <p className="text-xs text-destructive">{necThresholdError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {necThresholdUseDefault
                  ? "Will set all devices to use default from their client"
                  : "Enter an integer between 0 and 10000"}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-foreground">User Instructions</Label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bulkUserInstructionsUseDefault"
                  checked={userInstructionsUseDefault}
                  onChange={(e) => {
                    setUserInstructionsUseDefault(e.target.checked)
                    if (e.target.checked) {
                      setUserInstructions("unchanged")
                    }
                  }}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <Label htmlFor="bulkUserInstructionsUseDefault" className="text-xs text-muted-foreground cursor-pointer">
                  Use default
                </Label>
              </div>
            </div>
            <select
              value={userInstructions}
              onChange={(e) => {
                setUserInstructions(e.target.value as "unchanged" | "true" | "false")
                if (e.target.value !== "unchanged") {
                  setUserInstructionsUseDefault(false)
                }
              }}
              disabled={userInstructionsUseDefault}
              className="w-full px-3 py-2 text-sm rounded-md border border-input bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="unchanged">Keep unchanged</option>
              <option value="true">Enable</option>
              <option value="false">Disable</option>
            </select>
            {userInstructionsUseDefault && (
              <p className="text-xs text-muted-foreground">Will set all devices to use default from their client</p>
            )}
          </div>
        </div>

        {/* Affected devices list */}
        <div className="bg-background/50 rounded-lg border border-border p-3 mt-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Affected Devices
          </div>
          <div className="space-y-1 max-h-[100px] overflow-y-auto">
            {visibleDevices.map((deviceId) => (
              <div key={deviceId} className="text-sm font-mono text-foreground">
                {deviceId}
              </div>
            ))}
          </div>
          {hasMoreDevices && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllDevices(!showAllDevices)}
              className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/50 h-7"
            >
              {showAllDevices ? (
                <>
                  <ChevronUp className="h-3 w-3 mr-1" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3 mr-1" />
                  Show {deviceIds.length - 5} more
                </>
              )}
            </Button>
          )}
        </div>

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel
            disabled={isLoading}
            className="bg-secondary border-input text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              handleConfirm()
            }}
            disabled={isLoading || !hasChanges}
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isLoading ? "Updating..." : `Update ${deviceIds.length} Devices`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
